import * as React from 'react'
import {Component, ReactElement} from 'react'
import {ApplyHistoryInfo, NatGetApplyHistoryList} from '../repository/NatGetApplyHistoryList'
import {APPLY_CATEGORY, RESIDENT_APPLY_HISTORY_DIVISION, RESIDENT_APPLY_STATUS} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import styled from 'styled-components'
import {BreakPoint, Colors, FontSize, FontWeights} from '../../../../styledComponents/Constants'
import {generateMedia} from 'styled-media-query'
import {NatAsyncCallFunction} from '../../../common/util/Natutil'
import {createResidentPage, ResidentPagePropsSet} from '../../../../components/shared/hoc/createResidentPage'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'
import {Box, CardActionArea} from '@mui/material'
import {BlockSpacer, currentMedia} from '../../../../styledComponents/ResponsiveConvenience'
import {StyledPageTitle} from '../../../../components/reservations/components/CommonStyle'
import moment from 'moment'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {convertApplyStatusIntoApplyHistoryDivision} from '../common/helper/NatApplicationHelper'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import ScrollableCategorySelector from '../../../../components/shared/ScrollableCategorySelector'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {Location} from 'react-router-dom'

const KEY_APPLY_STATUS_FILTER_CONDITIONS = 'NatApplyHistoryListPage.filterConditions'

const applyCategoryKeyMap = new Map([
    [APPLY_CATEGORY.CONTACT_ADDRESS_ON_EMERGENCY.value, 'contactAddressOnEmergencies'],
    [APPLY_CATEGORY.PARKING_USE_APPLY.value, 'parkingUses'],
    [APPLY_CATEGORY.PARKING_USE_CANCEL.value, 'parkingUsesWithdrawal'],
    [APPLY_CATEGORY.CAR_CHANGE.value, 'parkingUsesChange'],
    [APPLY_CATEGORY.BICYCLE_PARKING_USE_APPLY.value, 'bicycleParkingUses'],
    [APPLY_CATEGORY.BICYCLE_PARKING_USE_ADMIT_CANCEL.value, 'bicycleParkingUsesWithdrawal'],
    [APPLY_CATEGORY.BIKE_PARKING_USE_APPLY.value, 'bikeParkingUses'],
    [APPLY_CATEGORY.BIKE_PARKING_USE_ADMIT_CANCEL.value, 'bikeParkingUsesWithdrawal'],
    [APPLY_CATEGORY.BIKE_CHANGE.value, 'bikeParkingUsesChange'],
    [APPLY_CATEGORY.BREEDING_PETS_APPLY.value, 'petBreeding'],
    [APPLY_CATEGORY.MEDICAL_EXAMINATION_REPORT.value, 'petMedicalCheckUpReport'],
    [APPLY_CATEGORY.BREEDING_PETS_CANCEL.value, 'petBreedingStop'],
    [APPLY_CATEGORY.CONSTRUCTION_APPLY.value, 'constructions'],
    [APPLY_CATEGORY.CONSTRUCTION_COMPLETION.value, 'constructionComplete'],
    [APPLY_CATEGORY.WINDOW_FILM_ATTACH_APPLY.value, 'windowFilmAttaches'],
    [APPLY_CATEGORY.LONG_ABSENCE.value, 'longAbsence'],
    [APPLY_CATEGORY.HEAVY_GOODS_APPLY.value, 'heavyLoadsCarries'],
    [APPLY_CATEGORY.INSPECTION_APPLY.value, 'browses'],
])

interface Props {
    condominiumCd: string,
    applicationRepo: NatApplicationRepo,
    currentDateTimeRepo: NatCurrentDateTimeRepo,
    location: Location
}

interface States {
    applyHistoryList: ApplyHistoryInfo[]
    filterConditions: FilterConditions
    isLoadCompleted: boolean
}

interface FilterConditions {
    applyStatusDivisions: RESIDENT_APPLY_HISTORY_DIVISION[]
}

class NatApplyHistoryListPage extends Component<Props & ResidentPagePropsSet, States> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)

        this.state = {
            applyHistoryList: [],
            filterConditions: {
                applyStatusDivisions: [],
            },
            isLoadCompleted: false,
        }
        this.handleChangeStatus = this.handleChangeStatus.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            this.setState({
                filterConditions: this.getResidentApplyFilterConditions(),
            })
            const searchConditionDate = ''
            // 申請履歴取得
            const applyHistoryList: Nullable<NatGetApplyHistoryList> =
                await NatAsyncCallFunction(
                    this.props.applicationRepo.getApplyHistoryList.bind(
                        this.props.applicationRepo,
                        condominiumCd,
                        searchConditionDate,
                    ), this.props.navigate)
            if (applyHistoryList && applyHistoryList.details !== null) {
                this.setState({applyHistoryList: applyHistoryList.details.applyInfo})
            }
        } finally {
            await eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    setResidentApplyFilterConditions(applyStatusDivisions: RESIDENT_APPLY_HISTORY_DIVISION[]): void {
        // セッションストレージに、選択した（されている）絞り込みデータをセット
        const filterConditions = applyStatusDivisions.map(applyStatusDivision => {
            return applyStatusDivision.value
        })

        SessionStorageHelper.setItem(
            KEY_APPLY_STATUS_FILTER_CONDITIONS,
            JSON.stringify(filterConditions),
        )
    }

    getResidentApplyFilterConditions(): FilterConditions {
        const json: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_APPLY_STATUS_FILTER_CONDITIONS)

        if (json) {
            const filterConditionsForJson: string[] = JSON.parse(json)
            return {
                applyStatusDivisions: filterConditionsForJson
                    .map(conditionApplyDivision =>
                        RESIDENT_APPLY_HISTORY_DIVISION.codeToEnumConstants(conditionApplyDivision)),
            }
        }
        return {applyStatusDivisions: []}
    }

    handleChangeStatus(selectedStatus: RESIDENT_APPLY_HISTORY_DIVISION[]): void {
        const {filterConditions} = this.state
        filterConditions.applyStatusDivisions = selectedStatus
        this.setResidentApplyFilterConditions(selectedStatus)
        this.setState({
            filterConditions: filterConditions,
        })
    }

    handleApplyHistoryCardClick(applyCategory: string, applyId: string): void {
        // 履歴カードクリック時のURL生成
        const {navigate, condominiumCd} = this.props
        const applyHistoryCategory = applyCategoryKeyMap.get(applyCategory)
        const url = `/resident/condominiums/${condominiumCd}/applications/${applyHistoryCategory}/${applyId}`
        navigate(url)
    }

    getApplyHistoryCategoryLabel(applyCategoryId: string): Nullable<ReactElement> {
        // 申請カテゴリIDからラベルを取得
        let applyHistoryCategoryLabel: any
        applyHistoryCategoryLabel = APPLY_CATEGORY.codeToEnumConstants(applyCategoryId)
        if (applyHistoryCategoryLabel) {
            return (
                <>{applyHistoryCategoryLabel.label}</>
            )
        } else {
            return null
        }
    }

    getApplyStatusLabel(applyStatus: string): ReactElement {
        return (
            <div>{convertApplyStatusIntoApplyHistoryDivision(applyStatus).label}</div>
        )
    }

    getUserInfo(applyUserFamilyName: string, applyUserFirstName: string): string {
        // 申請者名の情報
        return applyUserFamilyName + ' ' + applyUserFirstName
    }

    renderApplyStatusFilter(): ReactElement {
        // 絞り込みエリア
        const {filterConditions} = this.state
        const statues = filterConditions.applyStatusDivisions

        return (
            <FilterContainer>
                <ScrollableCategorySelector
                    onChangeCategory={this.handleChangeStatus}
                    categoryList={RESIDENT_APPLY_HISTORY_DIVISION.values()}
                    selectedCategoryList={statues}
                    displayAllButton
                />
            </FilterContainer>
        )
    }

    renderApplyHistory(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        // 申請履歴エリア
        const {filterConditions} = this.state
        const conditionInfos = filterConditions.applyStatusDivisions.length > 0 ?
            filterConditions.applyStatusDivisions :
            RESIDENT_APPLY_HISTORY_DIVISION.values()

        const filteredApplyHistory = this.state.applyHistoryList
            .filter(applyHistory => conditionInfos
                .find(condition => condition.value ===
                    convertApplyStatusIntoApplyHistoryDivision(applyHistory.applyStatus).value),
            )

        return (
            <CardArea>
                {(filteredApplyHistory.length > 0) ?
                    filteredApplyHistory.map((history, index) =>
                        <ApplyHistoryCard key={index} onClick={() => {
                            this.handleApplyHistoryCardClick(
                                history.applyCategory,
                                history.applyId)
                        }}>
                            <ApplyDateTime>
                                {moment(history.applyDatetime).format('YYYY年M月D日 HH:mm')} に提出
                                <StatusTdLabel applyStatus={history.applyStatus}>
                                    {this.getApplyStatusLabel(history.applyStatus)}
                                </StatusTdLabel>
                            </ApplyDateTime>
                            <ApplyID>
                                {history.applyId}
                            </ApplyID>
                            <CategoryCardTitle>
                                <CategoryCardTitleInner>
                                    {this.getApplyHistoryCategoryLabel(history.applyCategory)}
                                </CategoryCardTitleInner>
                            </CategoryCardTitle>
                            <UserName>
                                {this.getUserInfo(
                                    history.applyUserFamilyName,
                                    history.applyUserFirstName)}
                            </UserName>
                        </ApplyHistoryCard>,
                    ) : <>該当する申請はありません</>
                }
            </CardArea>
        )
    }

    render(): Nullable<ReactElement> {

        return (
            <>
                <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    届出・申請
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <StyledPageTitle>
                    申請履歴
                </StyledPageTitle>
                <Box height={'20px'}/>
                {this.renderApplyStatusFilter()}
                <Box height={'30px'}/>
                {this.renderApplyHistory()}
            </>
        )
    }
}

export default createResidentPage(NatApplyHistoryListPage)

// ブレイクポイント
const breakPoint: any = generateMedia({
    sp: `${BreakPoint.Sp}`,
})

const FilterContainer = styled.div`
  background-color: ${Colors.Surface};
  width: calc(100% + 30px);
  margin: 0 -15px;
`

const CardArea = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: 738px; /* 337px * 2 + 16px * 4 */
  margin: 0 auto;
`

const ApplyHistoryCard = styled(CardActionArea)`
  &.MuiCardActionArea-root {
    display: table;
    max-width: 354px;
    min-width: 330px;
    height: 130px;
    color: ${Colors.Body};
    background-color: ${Colors.Surface};
    font-size: ${FontSize.Small};
    line-height: 24px;
    margin: 0px 0px 20px 0px;
    box-shadow: 0 1px 8px 0 rgba(112, 112, 112, 0.4);
    ${currentMedia.lessThan('sp')`
      margin: 0 auto 20px auto;
  `};
  }
`

interface ApplyHistoryCssParams {
    applyStatus: string
}

const applyStatusColor = (props: ApplyHistoryCssParams) => {
    if (props.applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value) {
        return `background: linear-gradient(166deg, #009188 50%, #00838A 50%);`
    } else if (props.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
        return `background: #707070;`
    } else {
        return 'background: linear-gradient(166deg, #E76608 50%, #CF5A00 50%);'
    }
}

const StatusTdLabel = styled.div<ApplyHistoryCssParams>`
  color: #fff;
  text-align: center;
  font-size: ${FontSize.Small};
  display: table-cell;
  height: 18px;
  width: 70px;
  line-height: normal;

  ${(props: ApplyHistoryCssParams) => applyStatusColor(props)}

  ${breakPoint.lessThan('sp')`
   vertical-align: text-top;
`}
`
const ApplyDateTime = styled.div`
  display: flex;
  padding: 6px 10px 6px 10px;
  height: 18px;
  justify-content: space-between;
  background: rgba(217, 217, 217, 0.2);
`
const ApplyID = styled.div`
  display: block;
  height: 21px;
  padding: 10px 0px 0px 10px;
`

const CategoryCardTitle = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 0px 10px 0 10px;
`

const CategoryCardTitleInner = styled.div`
  display: block;
  vertical-align: middle;
  color: ${Colors.Body};
  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
`

const UserName = styled.div`
  display: block;
  height: 21px;
  vertical-align: middle;
  padding 10px 0px 15px 10px;
`
